import {Component} from 'panel';
import produce from 'immer';
import SmoothScroll from 'smooth-scroll';
import sortBy from 'lodash/sortBy';

import {fetchEventDefinitions, fetchPropertyDefinitions} from 'common/util/definitions';
import {dataGroupIDsEqual} from 'common/util';
import {baseComparator} from 'common/util/array';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import * as CustomEventUtils from 'common/util/custom_events';
import {customEventsClient} from 'common/report/queries/app-api/custom-events-client';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {eventPropertiesClient} from 'common/report/queries/query-api/event-properties-client';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {lookupTablesClient} from 'common/report/queries/app-api/lookup-tables-client';
import {peoplePropertiesClient} from 'common/report/queries/query-api/people-properties-client';
import {peoplePropertyValuesClient} from 'common/report/queries/query-api/people-property-values-client';
import {topEventsClient} from 'common/report/queries/query-api/top-events-client';
import {EVENT_SORT_TYPE} from 'common/report/queries/query-api/utils';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {customEventsStore} from 'common/report/stores/custom-events';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {storeCustomPropertyToProperty} from 'common/widgets/query-block/properties-menu/util';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {featuresStore} from 'common/report/stores/features';
import {getProjectId} from 'common/report/util';
import {getEntryFromLiteral, getLiteralFromEntry} from 'common/report/util/cohorts/transformations/entries';
import {reporter} from 'common/report/error-reporting';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';

import './builder-pane-dropdown/index';
import './literal-builder';
import {StagedCohort} from './models/builder/staged-cohort';
import {Cohort} from './models/cohort';
import {MAX_LITERALS} from './models/constants';
import {LogicalOperator, PredefinedCohort, Property} from './models/enums/expression-enums';
import {closestEl, hasNewQBEnabled, isDefinedValue, renameEvent, sorted} from './util';
import {CohortsMPContext} from './util/mp-context';
import {EVENTS_TO_TRACK_IN_MIXPANEL_PROD, getLookupTableTrackingProperties, TrackingEvent} from './util/tracking';
import template from './index.jade';
import css from './index.styl?inline';

export class StagedCohortsQueryBuilder extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'staged-cohort': {
        type: `json`,
        default: null,
        description: `the serialized staged cohort you want this builder to render`,
      },
      'project-id': {type: `number`},
      'project-has-cohorts': `boolean`,
      'data-group-id': {
        type: `string`,
        default: null,
        description: `the data group id that this cohort-builder queries with`,
      },
      'support-b2b-cohorts': `boolean`,
      disabled: {type: `boolean`, description: `whether this builder can be edited`},
      'hide-reset-button': {
        type: `boolean`,
        description: `don't include a reset button in the query builder`,
      },
      'track-name': `string`,
      'user-id': {type: `number`, description: `ID of user viewing the builder`},
      'workspace-id': {type: `number`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    // Be mindful of using this.update({stagedCohort}) vs. this.updateStagedCohort(stagedCohort). The latter should be
    // preferred since it dispatches events which may cause the parent component to update the staged-cohort attribute.
    // If an attribute change is deferred it can lead to bugs that are hard to track down
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `staged-cohort`:
        this.updateStagedCohortFromAttrs();
        break;
      case `data-group-id`:
        if (this.initialized) {
          this.setDataGroupId(newValue);
        }
        break;
    }
  }

  get projectId() {
    return this.attr(`project-id`);
  }

  get workspaceId() {
    return this.attr(`workspace-id`);
  }

  track(event, properties = {}) {
    this.dispatchEvent(
      new CustomEvent(`track`, {
        bubbles: true,
        composed: true,
        detail: {
          event,
          properties,
          isProductionEvent: EVENTS_TO_TRACK_IN_MIXPANEL_PROD.has(event),
        },
      }),
    );
  }

  set context(context) {
    this._context = context;
  }

  get context() {
    return this._context;
  }

  constructor() {
    super(...arguments);
    if (!this.context) {
      this.context = new CohortsMPContext();
    }

    this.storeUpdate = () => this.update();
  }

  async connectedCallback() {
    super.connectedCallback();

    await customPropertiesStore.fetch({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });

    await customEventsStore.fetch({
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });

    this.fetchLookupTables();

    this.fetchDefinitions();

    this.fetchEventProperties();

    this.setDataGroupId(this.attr(`data-group-id`));

    // We have a dependency upon mp-context and the features enabled for the current project. mp-chrome-header will initiate the fetch
    // we just need to listen for when the values update or change.
    useStore(this, featuresStore);
    useStore(this, customEventsStore);
    useStore(this, currentProjectStore, () => {
      // setDataGroupId is dependent on currentProjectStore.value.hasIntegratedEngage.
      // We can safely rerun this method with an unpopulated/populated currentProjectStore
      this.setDataGroupId(this.attr(`data-group-id`));
    });
  }

  async fetchEventProperties(eventName = ``) {
    if (this.state.eventProperties.has(eventName)) {
      return this.state.eventProperties.get(eventName);
    }

    let eventProperties = [];

    try {
      this.update(produce((state) => state.loading.eventProperties.set(eventName, true)));
      eventProperties = await eventPropertiesClient.query({
        ...(eventName ? {eventName} : {}),
        projectId: this.projectId,
        includeDimensionProperties: true,
        workspaceId: this.workspaceId,
      });

      eventProperties = [...eventProperties, ...this.getCustomProperties(ResourceType.Event)].sort(
        baseComparator({
          transform: (item) => item.label.toLowerCase(),
        }),
      );

      this.update(
        produce((state) => {
          state.loading.eventProperties.set(eventName, false);
          state.eventProperties.set(eventName, eventProperties);
        }),
      );
    } catch (error) {
      console.error(`Error loading event properties for cohort (${eventName})`, error);
    }

    return eventProperties;
  }

  isVisibleCustomProperty(customPropertyId) {
    const customProperty = customPropertiesStore.value[customPropertyId];
    return (
      !isPersistedCustomProperty(customPropertyId) ||
      customProperty.isVisible ||
      customProperty.user === this.attr(`user-id`)
    );
  }

  getCustomProperties(resourceType) {
    return Object.keys(customPropertiesStore.value)
      .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId))
      .map((customPropertyId) => {
        const property = storeCustomPropertyToProperty(customPropertyId);

        const {customProperty, value, type, resourceType} = property;

        return {
          count: 1,
          customProperty,
          customPropertyId: property.customPropertyId,
          label: value,
          name: value,
          type,
          resourceType,
        };
      })
      .filter((item) => item.resourceType === resourceType);
  }

  setDataGroupId(dataGroupId) {
    // Stores the last call's dataGroupId arg to prevent race condition bugs from rapid attribute changes
    // (e.g., Explore instantiates this component without a data-group-id and adds it after the session loads)
    this.dataGroupId = dataGroupId;
    this.update({loading: {...this.state.loading, topEvents: true, peopleProperties: true}});

    topEventsClient
      .query({
        dataGroupId,
        projectId: this.projectId,
        sortType: EVENT_SORT_TYPE.USAGE,
        workspaceId: this.workspaceId,
      })
      .then((topEvents) => {
        if (this.dataGroupId !== dataGroupId) {
          return;
        }
        const loading = {...this.state.loading, topEvents: false};
        this.update({topEvents, loading});
      });

    const projectHasPeople = currentProjectStore.value?.hasIntegratedEngage && !this.context.isBlockedOnPeople;

    if (projectHasPeople) {
      peoplePropertiesClient
        .query({
          dataGroupId,
          projectId: this.projectId,
          includeDimensionProperties: true,
          workspaceId: this.workspaceId,
        })
        .then((peopleProperties) => {
          if (this.dataGroupId !== dataGroupId) {
            return;
          }
          peopleProperties = [
            ...peopleProperties,
            {
              name: `$mp_first_event_time`,
              label: `First Seen`,
              icon: `calendar`,
              type: `datetime`,
            },
            ...this.getCustomProperties(ResourceType.User),
          ].sort(baseComparator({transform: (item) => item.label.toLowerCase()}));

          const loading = {...this.state.loading, peopleProperties: false};
          this.update({peopleProperties, loading});
        });
    } else {
      const loading = {...this.state.loading, peopleProperties: false};
      this.update({loading, projectHasPeople: false});
    }
    Promise.all([this.fetchCustomEvents(dataGroupId), this.fetchCohorts(dataGroupId), this.fetchDataGroups()]).then(
      ([customEvents, cohorts]) => {
        if (this.dataGroupId !== dataGroupId) {
          return;
        }

        if (!dataGroupIDsEqual(dataGroupId, this.state.stagedCohort.dataGroupId)) {
          const newStagedCohort = this.state.stagedCohort.makeValidForDataGroup({dataGroupId, customEvents, cohorts});
          this.updateStagedCohort(newStagedCohort);
        }

        const loading = {...this.state.loading, customEvents: false, cohorts: false};
        this.update({cohorts, customEvents, loading});

        this.updateStagedCohortDependentCohorts();
      },
    );
  }

  async fetchCustomEvents(dataGroupId) {
    this.update({loading: {...this.state.loading, customEvents: true}});
    return customEventsClient
      .query({
        projectId: this.projectId,
        workspaceId: this.workspaceId,
      })
      .then((events) => {
        // entity-sharing : hide custom events that user does not have access to from menus
        events = events.filter((ev) => ev.can_view);

        if (dataGroupId) {
          events = events.filter((ev) => !CustomEventUtils.hasUserFilters(ev, events));
        }

        return sorted(
          events.map((ev) => ({...ev, custom: !ev.is_collect_everything_event})),
          {
            transform: (ev) => renameEvent(ev.name),
          },
        );
      });
  }

  hasMultipleConditionTypes() {
    // Event conditions are always available. If either people or cohorts conditions are possible, then multiple
    // condition types exist.
    return this.state.projectHasPeople || this.canUseCohorts();
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        cohorts: {},
        cursorOnGripper: false,
        [this.builderStateReference]: this.defaultBuilderScreenState,
        draggingLiteralIdx: null,
        previewingLiterals: new Set(),
        stagedCohort: new StagedCohort(),
        stagedClausePosition: {
          literalIdx: null,
          clauseIdx: null,
        },
        eventDefinitions: [],
        eventPropertyDefinitions: [],
        userPropertyDefinitions: [],
        topEvents: [],
        eventProperties: new Map(),
        peopleProperties: [],
        customEvents: [],
        loading: {
          cohorts: true,
          customEvents: true,
          eventProperties: new Map([[``, true]]),
          peopleProperties: true,
          topEvents: true,
          dimensions: true,
        },
        projectHasPeople: true,
        tooltipData: null,
        newCohortsQBEnabled: false,
      },

      helpers: {
        MAX_LITERALS,
        doStoresHaveValue: () => featuresStore.hasFetchedValue(),

        hasNewCohortsQB: () => hasNewQBEnabled(this.projectId),
        handleQBToggleSelect: (ev) => this.update({newCohortsQBEnabled: ev.detail.selected === `new_qb`}),
        handleNewLiteralUpdate: (ev, index) => {
          const {stagedCohort} = this.state;
          this.updateStagedCohort(stagedCohort.replaceLiteral(ev.detail.literal, index));
        },
        getQBToggleOptions: () => [
          {
            label: `Cohorts QB`,
            value: `legacy_qb`,
          },
          {
            label: `New QB`,
            tooltipLabel: `Experimental cohorts query builder UI, not customer facing`,
            value: `new_qb`,
          },
        ],

        isDefaultStagedCohort: () => {
          const currCohort = Cohort.createCohortFromStaged(this.state.stagedCohort);
          const defaultCohort = Cohort.createCohortFromStaged(new StagedCohort());
          return currCohort.isEqual(defaultCohort);
        },

        resetStagedCohort: () => {
          const {stagedCohort} = this.state;
          this.track(TrackingEvent.ResetCohort, stagedCohort.toTrackingData());
          this.updateStagedCohort(new StagedCohort());
        },

        addLiteral: () => {
          const {stagedCohort} = this.state;
          this.track(TrackingEvent.AddLiteral, stagedCohort.toTrackingData());
          this.updateStagedCohort(stagedCohort.addLiteral());
        },

        isLiteralEndingAndBlock: (literalIdx) => {
          const operators = this.state.stagedCohort.operators;
          return (
            literalIdx > 0 &&
            operators[literalIdx - 1] === LogicalOperator.And &&
            (literalIdx >= operators.length || operators[literalIdx] !== LogicalOperator.And)
          );
        },

        isLiteralInMiddleAndBlock: (literalIdx) => {
          const operators = this.state.stagedCohort.operators;
          return (
            literalIdx > 0 &&
            operators[literalIdx - 1] === LogicalOperator.And &&
            !this.helpers.isLiteralStartingAndBlock(literalIdx) &&
            !this.helpers.isLiteralEndingAndBlock(literalIdx)
          );
        },

        isLiteralStartingAndBlock: (literalIdx) => {
          const operators = this.state.stagedCohort.operators;
          return (
            literalIdx < operators.length &&
            operators[literalIdx] === LogicalOperator.And &&
            (literalIdx === 0 || operators[literalIdx - 1] !== LogicalOperator.And)
          );
        },

        isLiteralWithinAndBlock: (literalIdx) => {
          return (
            this.helpers.isLiteralStartingAndBlock(literalIdx) ||
            this.helpers.isLiteralEndingAndBlock(literalIdx) ||
            this.helpers.isLiteralInMiddleAndBlock(literalIdx)
          );
        },

        isPreviewingOperation: (literalIdx) => this.state.previewingLiterals.has(literalIdx),

        onBuilderDragOver: (ev) => {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = `move`;
        },

        onLiteralDragOver: (ev) => {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = `move`;
          const closestLiteralEl = closestEl(ev.target, `.cohorts-literal`, `.cohorts-builder-body`);
          const toLiteralIdx = Number(closestLiteralEl.querySelector(`[elref="literalBuilder"]`).dataset.literalIdx);
          if (this.shouldMoveLiteral(ev, toLiteralIdx)) {
            const {stagedCohort, draggingLiteralIdx} = this.state;
            this.updateStagedCohort(stagedCohort.moveLiteral(draggingLiteralIdx, toLiteralIdx));
            this.update({draggingLiteralIdx: toLiteralIdx});
          }
        },

        onLiteralDragStart: (ev, literalIdx) => {
          ev.dataTransfer.setData(`text/plain`, null);
          ev.dataTransfer.effectAllowed = `move`;
          this.dragStartIndex = literalIdx;
          this.update({draggingLiteralIdx: literalIdx});
        },

        onLiteralDragEnd: () => {
          const {stagedCohort, draggingLiteralIdx} = this.state;
          if (draggingLiteralIdx) {
            this.track(
              TrackingEvent.MoveLiteral,
              Object.assign(
                {},
                stagedCohort.toTrackingData(),
                {'from index': this.dragStartIndex, 'to index': draggingLiteralIdx},
                stagedCohort.getTrackingDataForLiteral(draggingLiteralIdx),
              ),
            );
          }

          this.update({draggingLiteralIdx: null});
          this.updateStagedCohort(stagedCohort.updateLabelPrefixes());
        },

        onLiteralDrop: (ev) => ev.preventDefault(),

        previewOperation: (operatorIdx) => {
          const operators = this.state.stagedCohort.operators;
          let previewList = [operatorIdx, operatorIdx + 1];
          if (operators[operatorIdx] === LogicalOperator.Or) {
            for (let i = operatorIdx - 1; i >= 0 && operators[i] === LogicalOperator.And; i--) {
              previewList = previewList.concat([i]);
            }
            for (let i = operatorIdx + 1; i < operators.length && operators[i] === LogicalOperator.And; i++) {
              previewList = previewList.concat([i + 1]);
            }
          }

          this.update({previewingLiterals: new Set(previewList)});
        },

        stopPreviewingOperation: () => {
          this.update({previewingLiterals: new Set()});
        },

        toggleOperator: (operatorIdx) => {
          const {stagedCohort, previewingLiterals} = this.state;
          const groupingLiterals = stagedCohort.operators[operatorIdx] === LogicalOperator.Or;
          if (groupingLiterals) {
            this.track(
              TrackingEvent.GroupLiterals,
              Object.assign({}, stagedCohort.toTrackingData(), {'block size': previewingLiterals.size}),
            );
          } else {
            this.track(TrackingEvent.UngroupLiterals, stagedCohort.toTrackingData());
          }

          this.updateStagedCohort(stagedCohort.toggleOperator(operatorIdx));
          this.update({previewingLiterals: new Set()});
        },
      },
    };
  }

  shouldMoveLiteral(ev, toLiteralIdx) {
    if (this.state.draggingLiteralIdx === toLiteralIdx) {
      return false;
    }

    const cursorOffsetFromBuilderTop = ev.pageY - this.cohortBuilderOffsetTop - ev.currentTarget.offsetTop;
    const isCursorOnTopHalfOfTarget = cursorOffsetFromBuilderTop / ev.currentTarget.clientHeight < 0.5;
    const isNotWithinAndBlock = !this.helpers.isLiteralWithinAndBlock(toLiteralIdx);
    const isMovingToEdge =
      (toLiteralIdx === 0 && isCursorOnTopHalfOfTarget) ||
      (toLiteralIdx === this.state.stagedCohort.numLiterals - 1 && !isCursorOnTopHalfOfTarget);
    const isMovingFromAbove = this.state.draggingLiteralIdx < toLiteralIdx && !isCursorOnTopHalfOfTarget;
    const isMovingFromBelow = this.state.draggingLiteralIdx > toLiteralIdx && isCursorOnTopHalfOfTarget;
    const isMovingAboveAndBlock = isMovingFromBelow && this.helpers.isLiteralStartingAndBlock(toLiteralIdx);
    const isMovingBelowAndBlock = isMovingFromAbove && this.helpers.isLiteralEndingAndBlock(toLiteralIdx);
    return (
      (isMovingFromBelow || isMovingFromAbove) &&
      (isNotWithinAndBlock || isMovingToEdge || isMovingAboveAndBlock || isMovingBelowAndBlock)
    );
  }

  get builderStateReference() {
    return `builderScreen`;
  }

  get defaultBuilderScreenState() {
    return {
      activeIdx: 0,
      builderType: null,
      isOpen: false,
      panes: [],
      searchFilter: ``,
    };
  }

  updateBuilderState(attrs) {
    this.update({builderScreen: Object.assign({}, this.state.builderScreen, attrs)});
  }

  isBuilderOpen(builderType, clausePosition, propertyFilterIdx = null) {
    const {literalIdx, clauseIdx} = this.state.stagedClausePosition || {};
    const openPropertyFilterIdx = isDefinedValue(this.state.builderScreen.propertyFilterIdx)
      ? this.state.builderScreen.propertyFilterIdx
      : null;
    return (
      this.state.builderScreen.isOpen &&
      this.state.builderScreen.builderType === builderType &&
      clauseIdx === clausePosition.clauseIdx &&
      literalIdx === clausePosition.literalIdx &&
      propertyFilterIdx === openPropertyFilterIdx
    );
  }

  closeBuilder(builderType, clausePosition, propertyFilterIdx = null) {
    // if user is switching between builders, don't close
    if (this.isBuilderOpen(builderType, clausePosition, propertyFilterIdx)) {
      this.update({builderScreen: this.defaultBuilderScreenState});
    }
  }

  startClauseBuilderAtPane(
    startingComponent,
    builderType,
    stagedClausePosition,
    {previousScreens = [], builderAttrs = {}} = {},
  ) {
    const panes = [...previousScreens, startingComponent].map((componentName) => ({componentName}));

    const builderScreen = {
      ...this.defaultBuilderScreenState,
      ...builderAttrs,
      builderType,
      isOpen: true,
      panes,
    };
    this.update({builderScreen, stagedClausePosition});

    // timeout is required here because there are multiple update cycles before builder is rendered
    setTimeout(() => {
      const builderDropMenu = this.el.querySelector(`cohorts-builder-pane-dropdown[open] mp-drop-menu`);
      if (builderDropMenu) {
        const {bottom} = builderDropMenu.getBoundingClientRect();
        const windowBottom = window.scrollY + window.innerHeight;
        if (windowBottom < bottom) {
          new SmoothScroll().animateScroll(builderDropMenu, {speed: 250});
        }
      }
    }, 150);
  }

  get cohortBuilderOffsetTop() {
    return this.getBoundingClientRect().top + window.scrollY;
  }

  fetchPropertyValues(propertyName, propertyType, dataGroupId, customProperty, eventName) {
    const commonQueryProps = {
      projectId: this.projectId,
      ...(customProperty ? {customProperty: {customProperty}} : {propertyName}),
      ...(eventName ? {eventName: [eventName]} : {}),
      dataGroupId: dataGroupId || this.attr(`data-group-id`),
      includeDimensionProperties: true,
      workspaceId: this.workspaceId,
    };
    let fetchValuesPromise;
    if (propertyType === Property.User) {
      fetchValuesPromise = peoplePropertyValuesClient.query(commonQueryProps);
    } else {
      fetchValuesPromise = eventPropertyValuesClient.query(commonQueryProps);
    }

    return fetchValuesPromise.catch((error) => {
      console.warn(`Failed to fetch property values: ${error}`);
      // TODO: consider adding error UI, swallow errors for now
      return [];
    });
  }

  async fetchPeopleProperties(dataGroupId, includeDimensionProperties = true) {
    return peoplePropertiesClient.query({
      projectId: this.projectId,
      dataGroupId,
      includeDimensionProperties,
      workspaceId: this.workspaceId,
    });
  }

  async fetchDimensionProperties(dataGroupId) {
    this.update({loading: {...this.state.loading, dimensions: true}});
    const dimensionProperties = await this.fetchPeopleProperties(dataGroupId, true);

    this.update({dimensionProperties, loading: {...this.state.loading, dimensions: false}});
  }

  async fetchDataGroups() {
    if (!this.canUseDataGroups()) {
      return;
    }

    const groups = await dataGroupsClient.query({projectId: this.projectId, workspaceId: this.workspaceId});
    this.update({dataGroups: sortBy(groups, (g) => g.displayName)});
    const dataGroups = await Promise.all(
      this.state.dataGroups.map(async (dataGroup) => {
        let properties = await this.fetchPeopleProperties(dataGroup.groupID, false);
        if (this.dataGroupId === dataGroup.groupID) {
          properties = [
            {
              name: `$mp_first_event_time`,
              label: `First Seen`,
              icon: `calendar`,
              type: `datetime`,
              dataGroupId: dataGroup.groupID,
            },
            ...properties,
          ];
        }

        return {
          ...dataGroup,
          properties,
        };
      }),
    );

    this.update({dataGroups});
  }

  async fetchLookupTables() {
    const tables = Array.from(
      await lookupTablesClient.query({projectId: this.projectId, workspaceId: this.workspaceId}),
    );
    const lookupTables = {};
    for (const table of tables) {
      const dataGroupId = table[`id`];
      lookupTables[dataGroupId] = table;
    }
    this.update({lookupTables});
  }

  updateCustomPropertyDefinitions(definitions) {
    const customDefinitions = definitions
      .filter((definition) => definition.status === `custom`)
      // exclude deleted custom properties
      .filter((definition) => definition.customPropertyId in customPropertiesStore.value)
      // update from update-to-date custom property store, but keep other
      // information that custom property store doesn't have like query counts
      .map((definition) => {
        const customProperty = customPropertiesStore.value[definition.customPropertyId];
        return {
          ...definition,
          customProperty,
          description: customProperty.description || definition.description,
          name: customProperty.name || definition.name,
        };
      });

    const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

    return [...customDefinitions, ...regularDefinitions];
  }

  fetchDefinitions() {
    fetchEventDefinitions({projectId: this.projectId, workspaceId: this.workspaceId}).then((eventDefinitions) => {
      this.update({eventDefinitions});
    });

    fetchPropertyDefinitions({
      category: `events`,
      projectId: this.projectId,
      workspaceId: this.workspaceId,
      includeCustom: true,
    }).then((eventPropertyDefinitions) => {
      this.update({eventPropertyDefinitions: this.updateCustomPropertyDefinitions(eventPropertyDefinitions)});
    });

    fetchPropertyDefinitions({
      category: `user`,
      projectId: this.projectId,
      workspaceId: this.workspaceId,
      includeCustom: true,
    }).then((userPropertyDefinitions) => {
      this.update({userPropertyDefinitions: this.updateCustomPropertyDefinitions(userPropertyDefinitions)});
    });
  }

  async fetchCohorts(dataGroupId) {
    this.update({loading: {...this.state.loading, cohorts: true}, cohorts: {}});

    let results;
    if (this.canUseCohorts()) {
      results = await cohortsClient.query({
        projectId: this.projectId,
        workspaceId: this.workspaceId,
        ...(this.hasB2BCohorts() ? {dataGroupId} : {}),
      });
    } else {
      results = [];
    }

    return results.reduce(
      (acc, serializedCohort) => Object.assign(acc, {[serializedCohort.id]: Cohort.parseCohort(serializedCohort)}),
      {},
    );
  }

  getCohortById(cohortId, {includePredefined = true} = {}) {
    // this.state.cohorts doesn't exist yet during attributeChangedCallback
    const cohorts = this.state.cohorts || {};
    const cohort = cohorts[cohortId];
    if (cohort) {
      return cohort;
    }

    if (!includePredefined) {
      return null;
    }

    if (cohortId === PredefinedCohort.AllUsers) {
      return {
        id: PredefinedCohort.AllUsers,
        name: `All ${this.getActiveDataGroupLabel()}`,
      };
    }
  }

  updateStagedCohortFromAttrs() {
    const {id} = this.attr(`staged-cohort`) || {};
    let stagedCohort = StagedCohort.deserialize(this.attr(`staged-cohort`), {
      dependentCohortIds: this.getDependentCohortIds(id),
    });
    const behavior = stagedCohort && stagedCohort.getBehavior({literalIdx: 0, clauseIdx: 0});
    const literals = stagedCohort.literals;

    const projectId = parseInt(getProjectId(), 10);
    const TOP_COHORT_PROJECTS = new Set([1155510, 1667441, 349375, 697889, 1355125]);

    // Test Cohorts transformations in internal projects
    if (this.helpers.hasNewCohortsQB() || TOP_COHORT_PROJECTS.has(projectId)) {
      try {
        console.info(
          `Transformed Cohort`,
          literals.map((literal, literalIndex) => [
            getEntryFromLiteral(literal, {literalIndex, dataGroupId: stagedCohort.data_group_id, cohortId: id}),
            getLiteralFromEntry(
              getEntryFromLiteral(literal, {literalIndex, dataGroupId: stagedCohort.data_group_id, cohortId: id}),
            ),
          ]),
        );
      } catch (error) {
        reporter.warn(`Failed to convert query from Cohorts AST to common QB format`, {
          literals,
          error,
        });
      }
    }

    // Check if staged-cohort is a report-based (Funnels/Retention) cohort and load it inline
    if (behavior?.isReportBasedCohort()) {
      // Convert to inline/embedded cohort
      stagedCohort = new StagedCohort().updateCohort(stagedCohort.id, {literalIdx: 0, clauseIdx: 0});
      this.updateStagedCohort(stagedCohort, {queryReason: QueryReason.QbAutoTriggered});
      return;
    }

    this.update({
      stagedCohort,
    });
  }

  updateStagedCohortDependentCohorts() {
    const {id} = this.attr(`staged-cohort`) || {};
    this.state.stagedCohort.dependentCohortIds = this.getDependentCohortIds(id);
    this.update({stagedCohort: this.state.stagedCohort});
  }

  getDependentCohortIds(cohortId) {
    const cohort = cohortId ? this.getCohortById(cohortId) : null;
    return cohort ? cohort.dependentCohortIds : [];
  }

  canUseCohorts() {
    return this.attr(`project-has-cohorts`) || this.context.canUseCohorts();
  }

  updateStagedCohort(stagedCohort, {queryReason} = {}) {
    const origStagedCohort = this.state.stagedCohort;
    const origCohort = Cohort.createCohortFromStaged(origStagedCohort);
    const cohort = Cohort.createCohortFromStaged(stagedCohort);
    const cohortsEqual = origCohort.isEqual(cohort);

    this.update({stagedCohort});

    if (!cohortsEqual) {
      this.track(TrackingEvent.UpdateCohort, {
        ...stagedCohort.toTrackingData(),
        [commonTrackingEventPropertyName.dataGroupId]: this.attr(`data-group-id`),
        [commonTrackingEventPropertyName.filterByGroupProperty]: stagedCohort.literals.some((literal) =>
          literal.clauses.some((clause) => clause.propertyFilters.some((propertyFilter) => propertyFilter.dataGroupId)),
        ),
        [commonTrackingEventPropertyName.segmentByGroupProperty]: false, // Explore/cohorts only doesn't support segmenting
        [commonTrackingEventPropertyName.reportName]: this.attr(`track-name`),
        ...getLookupTableTrackingProperties(stagedCohort),
      });
      const detail = {
        cohort,
        queryReason: queryReason || QueryReason.QbOtherUpdate,
      };
      this.dispatchEvent(new CustomEvent(`cohortUpdated`, {detail}));
    }
    this.dispatchEvent(new CustomEvent(`stagedCohortUpdated`, {detail: {stagedCohort}}));
  }

  canUseDataGroups() {
    return this.context.canUseDataGroups();
  }

  hasDataGroups() {
    return this.canUseDataGroups() && this.state.dataGroups && this.state.dataGroups.length > 0;
  }

  hasMultiplePeopleGroups() {
    return this.canUseDataGroups() && Boolean(this.state.dataGroups?.length + (this.isAnalyzingUser() ? 1 : 0) > 1);
  }

  getDataGroupId() {
    if (!this.canUseDataGroups()) {
      return null;
    }
    return this.attr(`data-group-id`) || null;
  }

  getDataGroup(dataGroupId) {
    if (dataGroupId && this.hasDataGroups()) {
      return this.state.dataGroups.find((dg) => dg.groupID === dataGroupId);
    }
  }

  getDataGroupName(dataGroupId) {
    if (dataGroupId) {
      const dataGroup = this.getDataGroup(dataGroupId);
      return dataGroup ? dataGroup.displayName : `Group`;
    } else {
      return `User`;
    }
  }

  getActiveDataGroupLabel() {
    return `${this.getDataGroupName(this.attr(`data-group-id`))}(s)`;
  }

  isAnalyzingUser() {
    return !this.attr(`data-group-id`);
  }

  hasB2BCohorts() {
    return this.attr(`support-b2b-cohorts`);
  }

  getTrackName() {
    return this.attr(`track-name`);
  }
}

export const StagedCohortsQueryBuilderEl = defineAndMakeJsxFactory(
  `mp-staged-cohorts-query-builder`,
  StagedCohortsQueryBuilder,
);
